/**
 * ScalesFunctions.js
 * 
 * This module contains functions for scoring standardized self-report scales
 * used in mental health assessments.
 */

/**
 * ============================================================
 * Shared helpers (scoring engine building blocks)
 * ============================================================
 *
 * Why this exists:
 * - We are adding many new clinical scales over time.
 * - Historically each scale had bespoke logic, which makes it easy to introduce
 *   subtle inconsistencies (reverse-scoring rules, missing answer handling, etc.).
 *
 * Design goals:
 * - Keep existing scale outputs stable (especially AQ-10) while gradually moving
 *   scales onto a shared, definition-driven scoring workflow.
 * - Be robust to the two answer shapes we store in Firestore:
 *   - string: "Slightly Agree"
 *   - object: { value: "Slightly Agree", otherValue?: ... }
 *
 * Important:
 * - We do NOT change how/when scoring is persisted. PatientFormTemplate is the
 *   integration point: it calls `scoreScale()` and only persists scaleResults
 *   when `complete === true`.
 */

const getAnswerValue = (answerObj) => {
  if (answerObj && typeof answerObj === 'object') return answerObj.value;
  return answerObj;
};

/**
 * Extracts one answer per question for the common “single-select per question”
 * scale structure (AQ-10, AQ-50, etc.).
 *
 * Notes:
 * - We return an array aligned by question number (index 0 => Q1), even if some
 *   question objects are missing. This prevents a subtle bug where missing
 *   questions shift indices and corrupt scoring.
 */
const extractSingleSelectAnswersByQuestionNumber = (formData, questionCount, subsectionKey = '1') => {
  const answers = new Array(questionCount).fill(undefined);
  const subsection = formData?.formSubsections?.[subsectionKey];
  if (!subsection?.questions) return answers;

  for (let i = 1; i <= questionCount; i++) {
    const question = subsection.questions[i.toString()];
    const answerObj = question?.subquestions?.['1']?.answer;
    answers[i - 1] = getAnswerValue(answerObj);
  }

  return answers;
};

/**
 * Generic “AQ-style” binary scoring.
 *
 * Scoring rule (matches standard AQ instruments):
 * - Each item contributes either 0 or 1 point.
 * - Some items are reverse-scored (meaning agreement indicates autistic traits).
 *
 * We treat:
 * - reverse-scored item => 1 point for Agree responses
 * - normal-scored item  => 1 point for Disagree responses
 */
const scoreAqBinaryScale = ({
  formData,
  questionCount,
  reverseScoredQuestions,
}) => {
  const reverseSet = new Set(reverseScoredQuestions);
  const agree = new Set(["Definitely Agree", "Slightly Agree"]);
  const disagree = new Set(["Definitely Disagree", "Slightly Disagree"]);

  const answers = extractSingleSelectAnswersByQuestionNumber(formData, questionCount, '1');

  let totalScore = 0;
  let validAnswers = 0;

  answers.forEach((answer, index) => {
    if (!answer) return;
    validAnswers += 1;

    const qNumber = index + 1;
    const isReverse = reverseSet.has(qNumber);

    if (isReverse) {
      if (agree.has(answer)) totalScore += 1;
    } else {
      if (disagree.has(answer)) totalScore += 1;
    }
  });

  return { totalScore, validAnswers };
};

/**
 * CAT-Q / Likert helpers
 *
 * CAT-Q uses a 7-point Likert scale (1–7). A small set of items are reverse-scored,
 * meaning higher agreement indicates *less* camouflaging for those items.
 *
 * We keep this mapping explicit so it’s easy to audit and avoids “magic numbers”.
 */
const catqLikertMap = {
  "Strongly Disagree": 1,
  "Disagree": 2,
  "Somewhat Disagree": 3,
  "Neither Agree Nor Disagree": 4,
  "Somewhat Agree": 5,
  "Agree": 6,
  "Strongly Agree": 7,
};

const reverseLikertScore7 = (score) => {
  // For a 1..7 scale, reversing is simply 8 - score.
  return 8 - score;
};

/**
 * CORE-10 helpers
 *
 * CORE-10 uses a 5-point frequency scale scored 0–4. Two items are reverse scored.
 * We keep the answer strings identical to the ones stored in Firestore options.
 */
const core10LikertMap = {
  "Not at all": 0,
  "Only occasionally": 1,
  "Sometimes": 2,
  "Often": 3,
  "Most or all of the time": 4,
};

const reverseLikertScore4 = (score) => {
  // For a 0..4 scale, reversing is 4 - score.
  return 4 - score;
};

export const scoreAQ10 = (formData) => {
  if (!formData || !formData.formSubsections) return null;
  
  try {
    console.log(`[ScoreAQ10] Starting to score AQ-10`);

    // AQ-10 scoring configuration:
    // Reverse-scored items (1 point for Agree): 1, 7, 8, 10
    // Normal-scored items  (1 point for Disagree): all others
    //
    // IMPORTANT: We keep the output shape and interpretation wording identical
    // to the previous AQ-10 implementation to avoid breaking provider UI and
    // existing downstream assumptions.
    const { totalScore, validAnswers } = scoreAqBinaryScale({
      formData,
      questionCount: 10,
      reverseScoredQuestions: [1, 7, 8, 10],
    });
    
    // Only calculate if we have all 10 answers
    if (validAnswers !== 10) {
      return {
        scale: 'AQ-10',
        score: null,
        interpretation: null,
        severity: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/10 questions answered)`
      };
    }
    
    // Determine interpretation based on score
    const isPossibleAutism = totalScore >= 6;
    const interpretation = isPossibleAutism 
      ? "Possible autism spectrum disorder; further specialist assessment may be indicated" 
      : "Less likely to have autism spectrum disorder";
    
    const severity = isPossibleAutism ? "Clinically Significant" : "Not Clinically Significant";
    
    return {
      scale: 'AQ-10',
      score: totalScore,
      maxScore: 10,
      isPossibleAutism,
      interpretation,
      severity,
      complete: true
    };
  } catch (error) {
    console.error("Error scoring AQ-10:", error);
    return {
      scale: 'AQ-10',
      score: null,
      interpretation: null,
      severity: null,
      complete: false,
      error: error.message
    };
  }
};

/**
 * Scores AQ-50 (Autism Spectrum Quotient - 50 items).
 *
 * Interpretation thresholds (combined, since we may not know sex at scoring time):
 * - 36+ : Pronounced
 * - 26+ : Consistent with Autism
 *
 * Subscales (each 10 items, max score 10):
 * - Social Skills: Items 1, 11, 13, 15, 22, 36, 44, 45, 47, 48
 *   Assesses difficulties and discomfort with social situations.
 * - Attention Switching: Items 2, 4, 10, 16, 25, 32, 34, 37, 43, 46
 *   Describes difficulties in shifting focus between tasks or adapting to changes.
 * - Attention to Detail: Items 5, 6, 9, 12, 19, 23, 28, 29, 30, 49
 *   Relates to heightened focus on specific details and patterns.
 * - Communication: Items 7, 17, 18, 26, 27, 31, 33, 35, 38, 39
 *   Describes difficulties in reciprocal communication and interpreting social cues.
 * - Imagination: Items 3, 8, 14, 20, 21, 24, 40, 41, 42, 50
 *   Focuses on challenges related to imaginative thinking.
 *
 * Notes for future:
 * - Sex-specific thresholds (as provided):
 *   - Males:   26+ “Consistent with Autism”; 37+ “Pronounced”
 *   - Females: 27+ “Consistent with Autism”; 39+ “Pronounced”
 * - If/when we reliably have sex assigned at birth available at scoring time,
 *   we can switch to sex-specific thresholds.
 */
export const scoreAQ50 = (formData) => {
  if (!formData || !formData.formSubsections) return null;

  try {
    console.log(`[ScoreAQ50] Starting to score AQ-50`);

    // Reverse-scored items: Agree responses score 1 point
    // Source: https://docs.autismresearchcentre.com/papers/2001_BCetal_AQ.pdf
    // "Definitely agree" or "slightly agree" responses scored 1 point on items:
    // 1, 2, 4, 5, 6, 7, 9, 12, 13, 16, 18, 19, 20, 21, 22, 23, 26, 33, 35, 39, 41, 42, 43, 45, 46
    // Normal-scored items: Disagree responses score 1 point (all other items)
    const reverseScoredQuestions = new Set([
      1, 2, 4, 5, 6, 7, 9, 12, 13, 16, 18, 19, 20, 21, 22, 23, 26, 33, 35, 39, 41, 42, 43, 45, 46,
    ]);

    // Subscale item mappings (1-indexed question numbers)
    const subscales = {
      socialSkills: [1, 11, 13, 15, 22, 36, 44, 45, 47, 48],
      attentionSwitching: [2, 4, 10, 16, 25, 32, 34, 37, 43, 46],
      attentionToDetail: [5, 6, 9, 12, 19, 23, 28, 29, 30, 49],
      communication: [7, 17, 18, 26, 27, 31, 33, 35, 38, 39],
      imagination: [3, 8, 14, 20, 21, 24, 40, 41, 42, 50],
    };

    const agree = new Set(["Definitely Agree", "Slightly Agree"]);
    const disagree = new Set(["Definitely Disagree", "Slightly Disagree"]);

    const answers = extractSingleSelectAnswersByQuestionNumber(formData, 50, '1');

    // Calculate score for each question
    const questionScores = new Array(50).fill(null);
    let validAnswers = 0;

    answers.forEach((answer, index) => {
      if (!answer) return;
      validAnswers += 1;

      const qNumber = index + 1;
      const isReverse = reverseScoredQuestions.has(qNumber);

      let score = 0;
      if (isReverse) {
        if (agree.has(answer)) score = 1;
      } else {
        if (disagree.has(answer)) score = 1;
      }
      questionScores[index] = score;
    });

    if (validAnswers !== 50) {
      return {
        scale: 'AQ-50',
        score: null,
        interpretation: null,
        severity: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/50 questions answered)`,
      };
    }

    // Calculate total score
    const totalScore = questionScores.reduce((sum, s) => sum + (s || 0), 0);

    // Calculate subscale scores (each subscale has 10 items, max score 10)
    const subscaleScores = {};
    for (const [subscaleName, items] of Object.entries(subscales)) {
      const subscaleScore = items.reduce((sum, qNum) => sum + (questionScores[qNum - 1] || 0), 0);
      subscaleScores[subscaleName] = subscaleScore;
    }

    let severity = "Below Threshold";
    let interpretation = "Score is below the combined AQ-50 threshold for autism traits.";

    if (totalScore >= 36) {
      severity = "Pronounced";
      interpretation = "Score is consistent with pronounced autistic traits (combined threshold).";
    } else if (totalScore >= 26) {
      severity = "Consistent with Autism";
      interpretation = "Score is consistent with autistic traits (combined threshold).";
    }

    return {
      scale: 'AQ-50',
      score: totalScore,
      maxScore: 50,
      interpretation,
      severity,
      complete: true,
      // Subscale scores (each out of 10)
      socialSkillsScore: subscaleScores.socialSkills,
      attentionSwitchingScore: subscaleScores.attentionSwitching,
      attentionToDetailScore: subscaleScores.attentionToDetail,
      communicationScore: subscaleScores.communication,
      imaginationScore: subscaleScores.imagination,
      thresholds: {
        combined: { consistentWithAutism: 26, pronounced: 36 },
        // kept for reference/documentation (not used yet)
        males: { consistentWithAutism: 26, pronounced: 37 },
        females: { consistentWithAutism: 27, pronounced: 39 },
      },
    };
  } catch (error) {
    console.error("Error scoring AQ-50:", error);
    return {
      scale: 'AQ-50',
      score: null,
      interpretation: null,
      severity: null,
      complete: false,
      error: error.message,
    };
  }
};

/**
 * Scores CAT-Q (Camouflaging Autistic Traits Questionnaire).
 *
 * Summary of scoring rules (as provided):
 * - 25 items, each scored 1–7 (higher = greater camouflaging).
 * - Reverse-score items: 3, 12, 19, 22, 24 (1↔7, 2↔6, 3↔5, 4 stays).
 * - Total score = sum of all 25 items (range 25–175).
 * - Threshold: total >= 100 indicates camouflaging autistic traits.
 *
 * Subscales:
 * - Compensation: 1,4,5,8,11,14,17,20,23 (9 items)
 * - Masking: 2,6,9,12,15,18,21,24 (8 items; reverse applies to 12 & 24)
 * - Assimilation: 3,7,10,13,16,19,22,25 (8 items; reverse applies to 3,19,22)
 *
 * Storage/design notes:
 * - We store the total score AND the three subscale scores inside `scaleResults`
 *   so the data can be exported/analysed later.
 * - For the provider overview row display, we will show only whether the threshold
 *   was crossed (>=100) per your requirement, but the raw scores remain accessible
 *   in the details view.
 */
export const scoreCATQ = (formData) => {
  if (!formData || !formData.formSubsections) return null;

  try {
    console.log(`[ScoreCATQ] Starting to score CAT-Q`);

    const questionCount = 25;
    const reverseItems = new Set([3, 12, 19, 22, 24]);

    const compensationItems = [1, 4, 5, 8, 11, 14, 17, 20, 23];
    const maskingItems = [2, 6, 9, 12, 15, 18, 21, 24];
    const assimilationItems = [3, 7, 10, 13, 16, 19, 22, 25];

    const answers = extractSingleSelectAnswersByQuestionNumber(formData, questionCount, '1');

    const scoredByItem = {}; // itemNumber -> numericScore (after reverse scoring)
    let validAnswers = 0;

    answers.forEach((answer, idx) => {
      const itemNumber = idx + 1;
      if (!answer) return;

      const rawScore = catqLikertMap[answer];
      if (rawScore === undefined) return;

      validAnswers += 1;
      const finalScore = reverseItems.has(itemNumber) ? reverseLikertScore7(rawScore) : rawScore;
      scoredByItem[itemNumber] = finalScore;
    });

    if (validAnswers !== questionCount) {
      return {
        scale: 'CAT-Q',
        score: null,
        interpretation: null,
        severity: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/25 questions answered)`,
      };
    }

    const sumItems = (items) => items.reduce((sum, i) => sum + (scoredByItem[i] || 0), 0);

    const compensationScore = sumItems(compensationItems); // 9..63
    const maskingScore = sumItems(maskingItems); // 8..56
    const assimilationScore = sumItems(assimilationItems); // 8..56
    const totalScore = compensationScore + maskingScore + assimilationScore; // 25..175

    const threshold = 100;
    const isHighCamouflaging = totalScore >= threshold;

    const severity = isHighCamouflaging ? 'Threshold crossed' : 'Below threshold';
    const interpretation = isHighCamouflaging
      ? `Total score is ${totalScore}, which is at or above the CAT-Q threshold (${threshold}) indicating camouflaging autistic traits.`
      : `Total score is ${totalScore}, which is below the CAT-Q threshold (${threshold}).`;

    return {
      scale: 'CAT-Q',
      score: totalScore,
      maxScore: 175,
      complete: true,
      isHighCamouflaging,
      threshold,
      severity,
      interpretation,
      subscales: {
        compensation: { score: compensationScore, maxScore: 63, items: compensationItems },
        masking: { score: maskingScore, maxScore: 56, items: maskingItems },
        assimilation: { score: assimilationScore, maxScore: 56, items: assimilationItems },
      },
      // For auditing/debugging exports, it can be useful to know which items were reversed.
      reverseScoredItems: Array.from(reverseItems),
    };
  } catch (error) {
    console.error("Error scoring CAT-Q:", error);
    return {
      scale: 'CAT-Q',
      score: null,
      interpretation: null,
      severity: null,
      complete: false,
      error: error.message,
    };
  }
};

/**
 * Scores CORE-10 (Clinical Outcomes in Routine Evaluation – 10).
 *
 * Scoring (Connell & Barkham, 2007; Barkham et al., 2012):
 * - Each item scored 0–4.
 * - Reverse scored items: 2 and 3.
 * - Clinical score:
 *   - Sum item scores, divide by number of questions completed (answered),
 *     then multiply by 10.
 *   - If all 10 are answered, clinical score equals the raw sum (0–40).
 *
 * Completion rules in this platform:
 * - We allow users to *skip* questions. Skip writes `wasSkipped=true` at the question level
 *   and stores null answers. If a user finishes the form (progress 100%) with skips,
 *   we still want a score based on the items they DID answer.
 * - Therefore we mark `complete: true` when every item is either answered or skipped.
 */
export const scoreCORE10 = (formData) => {
  if (!formData || !formData.formSubsections) return null;

  try {
    console.log(`[ScoreCORE10] Starting to score CORE-10`);

    const questionCount = 10;
    const reverseItems = new Set([2, 3]);

    const answers = extractSingleSelectAnswersByQuestionNumber(formData, questionCount, '1');

    let answeredCount = 0;
    let sumScores = 0;

    for (let i = 1; i <= questionCount; i++) {
      const answer = answers[i - 1];
      if (!answer) continue;

      const raw = core10LikertMap[answer];
      if (raw === undefined) continue;

      answeredCount += 1;
      const finalScore = reverseItems.has(i) ? reverseLikertScore4(raw) : raw;
      sumScores += finalScore;
    }

    // Enforce mandatory completion: CORE-10 is only scored when ALL items are answered.
    // The user explicitly requested we do not allow skip-based completion for CORE-10.
    if (answeredCount !== questionCount) {
      return {
        scale: 'CORE-10',
        score: null,
        interpretation: null,
        severity: null,
        complete: false,
        message: `Incomplete assessment (${answeredCount}/${questionCount} questions answered)`,
      };
    }

    // When all 10 items are answered, the adjusted score (mean*10) equals the raw sum (0–40).
    const clinicalScore = sumScores;

    const cutoff = 11;
    const isClinicallySignificant = clinicalScore >= cutoff;

    // Interpretation bands. We compare using the rounded clinicalScore so what we display
    // matches the band we select (avoids “10.999 looks like 11” edge cases).
    let severity = 'Healthy';
    // CORE-10 severity bands - shortened labels for badge display
    if (clinicalScore <= 5) severity = 'Healthy';
    else if (clinicalScore <= 10) severity = 'Low level';
    else if (clinicalScore <= 14) severity = 'Mild';
    else if (clinicalScore <= 19) severity = 'Moderate';
    else if (clinicalScore <= 24) severity = 'Moderate to severe';
    else severity = 'Severe';

    const interpretation = isClinicallySignificant
      ? `Clinical score is ${clinicalScore}. This is at or above the clinical cut-off (${cutoff}), indicating clinically significant distress.`
      : `Clinical score is ${clinicalScore}. This is below the clinical cut-off (${cutoff}).`;

    return {
      scale: 'CORE-10',
      score: clinicalScore,
      maxScore: 40,
      complete: true,
      severity,
      interpretation,
      cutoff,
      isClinicallySignificant,
      answeredCount,
      raw: { sumScores },
    };
  } catch (error) {
    console.error('Error scoring CORE-10:', error);
    return {
      scale: 'CORE-10',
      score: null,
      interpretation: null,
      severity: null,
      complete: false,
      error: error.message,
    };
  }
};

/**
 * Scores ASRS (Adult ADHD Self-Report Scale v1.1 – 18 items).
 *
 * Important: ASRS uses “marked/shaded” response thresholds that vary by item.
 * This function implements the same validated rule-set used in
 * `python-functions/rst_functions.py`:
 *
 * - Group A (items scored 1 when response is Sometimes/Often/Very Often):
 *   1, 2, 3, 9, 12, 16, 18
 * - Group B (items scored 1 when response is Often/Very Often):
 *   4, 5, 6, 7, 8, 10, 11, 13, 14, 15, 17
 *
 * Score breakdown:
 * - Part A: first 6 items (Q1–Q6) scored using the above per-item thresholds.
 * - Part B: remaining 12 items (Q7–Q18).
 *
 * Interpretation:
 * - Part A score >= 4 often indicates symptoms highly consistent with ADHD
 *   and suggests further clinical evaluation.
 * - Part B has no standalone clinical interpretation but is recorded.
 */
export const scoreASRS = (formData) => {
  if (!formData || !formData.formSubsections) return null;

  try {
    console.log('[ScoreASRS] Starting to score ASRS');

    const questionCount = 18;

    const groupA = new Set([1, 2, 3, 9, 12, 16, 18]);
    const groupB = new Set([4, 5, 6, 7, 8, 10, 11, 13, 14, 15, 17]);

    const answers = extractSingleSelectAnswersByQuestionNumber(formData, questionCount, '1');

    let validAnswers = 0;
    let partAScore = 0; // Q1-6
    let partBScore = 0; // Q7-18
    let totalScore = 0; // total flagged responses (0-18)

    for (let i = 1; i <= questionCount; i++) {
      const answer = answers[i - 1];
      if (!answer) continue;
      validAnswers += 1;

      let scoresPoint = false;
      if (groupA.has(i)) {
        scoresPoint = answer === 'Sometimes' || answer === 'Often' || answer === 'Very Often';
      } else if (groupB.has(i)) {
        scoresPoint = answer === 'Often' || answer === 'Very Often';
      }

      if (scoresPoint) {
        totalScore += 1;
        if (i <= 6) partAScore += 1;
        else partBScore += 1;
      }
    }

    // Require all 18 items answered for a complete score (matches other brief screeners).
    if (validAnswers !== 18) {
      return {
        scale: 'ASRS',
        score: null,
        interpretation: null,
        severity: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/18 questions answered)`,
      };
    }

    const meetsCondition = partAScore >= 4;
    const interpretation = meetsCondition
      ? 'Part A score meets the ASRS threshold (≥4), indicating symptoms highly consistent with ADHD; further clinical evaluation is suggested.'
      : 'Part A score is below the ASRS threshold (≥4).';

    const severity = meetsCondition ? 'Threshold crossed' : 'Below threshold';

    return {
      scale: 'ASRS',
      score: totalScore,
      maxScore: 18,
      complete: true,
      partAScore,
      partBScore,
      threshold: 4,
      meetsCondition,
      interpretation,
      severity,
    };
  } catch (error) {
    console.error('Error scoring ASRS:', error);
    return {
      scale: 'ASRS',
      score: null,
      interpretation: null,
      severity: null,
      complete: false,
      error: error.message,
    };
  }
};

/**
 * Scores ASRS-O Informant (Adult ADHD Self-Report Scale - Observer/Informant version).
 *
 * This is the informant version of the ASRS, where an observer (e.g., family member,
 * partner) rates the individual's ADHD symptoms over the past 6 months.
 *
 * Scoring:
 * - Each of the 18 items is rated on a 5-point scale: Never (0), Rarely (1),
 *   Sometimes (2), Often (3), Very Often (4).
 * - Total score is the sum of all 18 items (range: 0-72).
 * - No clinical threshold or interpretation is provided; this is a dimensional
 *   measure that provides collateral information to support the clinical interview.
 *
 * DSM code mapping (for prefills):
 * - Q1→A1d, Q2→A1e, Q3→A1i, Q4→A1f, Q5→A2a, Q6→A2e, Q7→A1a, Q8→A1b,
 * - Q9→A1c, Q10→A1g, Q11→A1h, Q12→A2b, Q13→A2c, Q14→A2d, Q15→A2f,
 * - Q16→A2g, Q17→A2h, Q18→A2i
 */
export const scoreASRSOInformant = (formData) => {
  if (!formData || !formData.formSubsections) return null;

  try {
    console.log('[ScoreASRSOInformant] Starting to score ASRS-O Informant');

    const questionCount = 18;

    // Likert scale mapping: Never=0, Rarely=1, Sometimes=2, Often=3, Very Often=4
    const likertMap = {
      "Never": 0,
      "Rarely": 1,
      "Sometimes": 2,
      "Often": 3,
      "Very Often": 4,
    };

    const answers = extractSingleSelectAnswersByQuestionNumber(formData, questionCount, '1');

    let validAnswers = 0;
    let totalScore = 0;

    for (let i = 1; i <= questionCount; i++) {
      const answer = answers[i - 1];
      if (!answer) continue;

      const score = likertMap[answer];
      if (score !== undefined) {
        validAnswers += 1;
        totalScore += score;
      }
    }

    // Require all 18 items answered for a complete score
    if (validAnswers !== 18) {
      return {
        scale: 'ASRS-O Informant',
        score: null,
        interpretation: null,
        severity: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/18 questions answered)`,
      };
    }

    // No clinical threshold for ASRS-O Informant - just provide the total score
    // as collateral information to support clinical decision-making
    return {
      scale: 'ASRS-O Informant',
      score: totalScore,
      maxScore: 72,
      complete: true,
      interpretation: 'Collateral informant rating of ADHD symptoms. No clinical threshold; used to support clinical interview.',
      severity: null,
    };
  } catch (error) {
    console.error('Error scoring ASRS-O Informant:', error);
    return {
      scale: 'ASRS-O Informant',
      score: null,
      interpretation: null,
      severity: null,
      complete: false,
      error: error.message,
    };
  }
};

/**
 * Scores CNST (Combined Neurodevelopmental Screening Tool).
 *
 * Key design decision:
 * - The form model stores each condition as one "question" with many "subquestions"
 *   (statements). This keeps the UI grouping coherent and allows per-condition scoring.
 *
 * Scoring:
 * - Each statement is scored 0–3.
 * - For each condition, compute raw score, max score, percentage, and level:
 *   - low: <20%
 *   - moderate: 20–39%
 *   - high: >=40%
 */
export const scoreCNST = (formData) => {
  if (!formData || !formData.formSubsections) return null;

  try {
    console.log('[ScoreCNST] Starting to score CNST');

    const ratingMap = {
      "Not at all / Never": 0,
      "Occasionally / Sometimes": 1,
      "Frequently / Often": 2,
      "Almost always / Very frequently": 3,
    };

    const structure = [
      { key: 'dyslexia', questionIndex: '1', itemCount: 15 },
      { key: 'dyscalculia', questionIndex: '2', itemCount: 15 },
      { key: 'dcd', questionIndex: '3', itemCount: 10 },
      { key: 'tics', questionIndex: '4', itemCount: 8 },
      { key: 'dld', questionIndex: '5', itemCount: 12 },
    ];

    const subsection = formData.formSubsections?.['1'];
    const questions = subsection?.questions || {};

    const determineLevel = (percentage) => {
      if (percentage >= 40) return { level: 'high', label: 'High Possibility', description: 'Multiple significant indicators present' };
      if (percentage >= 20) return { level: 'moderate', label: 'Moderate Possibility', description: 'Some indicators present' };
      return { level: 'low', label: 'Low Possibility', description: 'Few indicators present' };
    };

    let questionsAnswered = 0;
    const totalQuestions = 60;

    const scores = {};

    for (const { key, questionIndex, itemCount } of structure) {
      const q = questions?.[questionIndex];
      const subqs = q?.subquestions || {};

      let rawScore = 0;
      let valid = 0;

      for (let i = 1; i <= itemCount; i++) {
        const answerObj = subqs?.[i.toString()]?.answer;
        const answer = getAnswerValue(answerObj);
        if (answer === undefined || answer === null || answer === '') {
          // Missing answer => incomplete scoring.
          continue;
        }
        if (ratingMap[answer] === undefined) continue;
        rawScore += ratingMap[answer];
        valid += 1;
      }

      questionsAnswered += valid;

      const maxScore = itemCount * 3;
      const percentage = maxScore > 0 ? (rawScore / maxScore) * 100 : 0;
      const levelObj = determineLevel(percentage);
      const recommendationsTriggered = levelObj.level !== 'low';

      scores[key] = {
        raw_score: rawScore,
        max_score: maxScore,
        percentage: Math.round(percentage * 10) / 10,
        level: levelObj.level,
        level_label: levelObj.label,
        description: levelObj.description,
        recommendations_triggered: recommendationsTriggered,
        answered: valid,
        total_items: itemCount,
      };
    }

    if (questionsAnswered !== totalQuestions) {
      return {
        scale: 'CNST',
        score: null,
        interpretation: null,
        severity: null,
        complete: false,
        message: `Incomplete assessment (${questionsAnswered}/${totalQuestions} statements answered)`,
        scores,
      };
    }

    const elevatedAreas = Object.keys(scores).filter(k => scores[k].level === 'high');
    const areasForMonitoring = Object.keys(scores).filter(k => scores[k].level === 'moderate');

    const highCount = elevatedAreas.length;
    const moderateCount = areasForMonitoring.length;
    const elevatedOrMonitoringCount = highCount + moderateCount; // 0..5

    return {
      scale: 'CNST',
      // Provider list UI expects a single numeric score for the progress bar; we use a compact summary:
      // number of conditions at least moderate.
      score: elevatedOrMonitoringCount,
      maxScore: 5,
      complete: true,
      severity: `High: ${highCount}, Moderate: ${moderateCount}`,
      interpretation: 'CNST results are reported per condition (low/moderate/high possibility).',
      scores,
      summary: {
        total_questions: totalQuestions,
        questions_answered: questionsAnswered,
        completion_percentage: 100,
        elevated_areas: elevatedAreas,
        areas_for_monitoring: areasForMonitoring,
      },
    };
  } catch (error) {
    console.error('Error scoring CNST:', error);
    return {
      scale: 'CNST',
      score: null,
      interpretation: null,
      severity: null,
      complete: false,
      error: error.message,
    };
  }
};

/**
 * Scores SPAT (Sensory Profile Assessment Tool).
 *
 * Key design decision:
 * - Like CNST, the form groups items by system as separate "questions" with many subquestions.
 * - Dunn quadrant mappings are implemented here (not in the form model) because the form model
 *   is display-focused and we want scoring logic centralized/maintainable in JS.
 *
 * Scoring:
 * - Each item is scored 1–5 (frequency).
 * - We compute:
 *   - quadrant_scores (sensitivity/seeking/avoiding/registration): raw, count, average, percentage, level
 *   - system_scores (visual/auditory/olfactory/tactile/vestibular/proprioceptive/interoceptive/pain): same + primary_quadrants
 * - Levels are derived from average (1–5):
 *   - high: >= 3.5 ("Much More Than Others")
 *   - moderate: >= 2.5 ("More Than Others")
 *   - typical: >= 1.5 ("Similar to Others")
 *   - low: < 1.5 ("Less Than Others")
 * - Clinical significance:
 *   - Any quadrant high OR 2+ quadrants moderate+
 */
export const scoreSPAT = (formData) => {
  if (!formData || !formData.formSubsections) return null;

  try {
    console.log('[ScoreSPAT] Starting to score SPAT');

    const ratingMap = {
      "Almost Never (0-20% of the time)": 1,
      "Seldom (21-40% of the time)": 2,
      "Occasionally (41-60% of the time)": 3,
      "Frequently (61-80% of the time)": 4,
      "Almost Always (81-100% of the time)": 5,
    };

    // Each system is one question index in the form model.
    const systems = [
      { key: 'visual', questionIndex: '1', itemCount: 8 },
      { key: 'auditory', questionIndex: '2', itemCount: 8 },
      { key: 'olfactory', questionIndex: '3', itemCount: 8 },
      { key: 'tactile', questionIndex: '4', itemCount: 8 },
      { key: 'vestibular', questionIndex: '5', itemCount: 8 },
      { key: 'proprioceptive', questionIndex: '6', itemCount: 7 },
      { key: 'interoceptive', questionIndex: '7', itemCount: 7 },
      { key: 'pain', questionIndex: '8', itemCount: 7 },
    ];

    // Dunn quadrant mapping (system -> 1-based item index -> quadrant).
    // This mirrors the customer-provided spec exactly.
    const quadrantMap = {
      visual: {
        1: 'sensitivity', 2: 'seeking', 3: 'registration', 4: 'avoiding',
        5: 'sensitivity', 6: 'seeking', 7: 'registration', 8: 'avoiding',
      },
      auditory: {
        1: 'sensitivity', 2: 'seeking', 3: 'registration', 4: 'avoiding',
        5: 'sensitivity', 6: 'seeking', 7: 'sensitivity', 8: 'registration',
      },
      olfactory: {
        1: 'sensitivity', 2: 'seeking', 3: 'registration', 4: 'avoiding',
        5: 'sensitivity', 6: 'seeking', 7: 'registration', 8: 'avoiding',
      },
      tactile: {
        1: 'sensitivity', 2: 'seeking', 3: 'registration', 4: 'avoiding',
        5: 'sensitivity', 6: 'seeking', 7: 'registration', 8: 'avoiding',
      },
      vestibular: {
        1: 'sensitivity', 2: 'seeking', 3: 'registration', 4: 'avoiding',
        5: 'sensitivity', 6: 'seeking', 7: 'registration', 8: 'avoiding',
      },
      proprioceptive: {
        1: 'registration', 2: 'seeking', 3: 'registration', 4: 'seeking',
        5: 'registration', 6: 'seeking', 7: 'registration',
      },
      interoceptive: {
        1: 'registration', 2: 'registration', 3: 'registration', 4: 'registration',
        5: 'registration', 6: 'sensitivity', 7: 'registration',
      },
      pain: {
        1: 'registration', 2: 'sensitivity', 3: 'registration', 4: 'sensitivity',
        5: 'seeking', 6: 'registration', 7: 'sensitivity',
      },
    };

    const determineLevelFromAverage = (avg) => {
      if (avg >= 3.5) return { level: 'high', label: 'Much More Than Others', description: 'Significantly elevated pattern' };
      if (avg >= 2.5) return { level: 'moderate', label: 'More Than Others', description: 'Moderately elevated pattern' };
      if (avg >= 1.5) return { level: 'typical', label: 'Similar to Others', description: 'Typical pattern' };
      return { level: 'low', label: 'Less Than Others', description: 'Lower than typical pattern' };
    };

    const subsection = formData.formSubsections?.['1'];
    const questions = subsection?.questions || {};

    const totalQuestions = 61;
    let questionsAnswered = 0;

    const quadrantAcc = {
      sensitivity: { raw: 0, count: 0 },
      seeking: { raw: 0, count: 0 },
      avoiding: { raw: 0, count: 0 },
      registration: { raw: 0, count: 0 },
    };

    const system_scores = {};

    for (const { key: systemKey, questionIndex, itemCount } of systems) {
      const q = questions?.[questionIndex];
      const subqs = q?.subquestions || {};

      let raw = 0;
      let count = 0;
      const localQuadrantRaw = { sensitivity: 0, seeking: 0, avoiding: 0, registration: 0 };

      for (let i = 1; i <= itemCount; i++) {
        const answerObj = subqs?.[i.toString()]?.answer;
        const answer = getAnswerValue(answerObj);
        if (answer === undefined || answer === null || answer === '') continue;
        const value = ratingMap[answer];
        if (value === undefined) continue;

        raw += value;
        count += 1;
        questionsAnswered += 1;

        const quadrant = quadrantMap?.[systemKey]?.[i];
        if (quadrant) {
          quadrantAcc[quadrant].raw += value;
          quadrantAcc[quadrant].count += 1;
          localQuadrantRaw[quadrant] += value;
        }
      }

      const maxPossible = count * 5;
      const average = count > 0 ? raw / count : 0;
      const percentage = maxPossible > 0 ? (raw / maxPossible) * 100 : 0;
      const levelObj = determineLevelFromAverage(average);

      // Primary quadrants: the top 1-2 quadrants by contribution within this system.
      const sortedQuadrants = Object.entries(localQuadrantRaw)
        .sort((a, b) => b[1] - a[1])
        .filter(([, v]) => v > 0)
        .map(([k]) => k);
      const primary_quadrants = sortedQuadrants.slice(0, 2);

      system_scores[systemKey] = {
        raw_score: raw,
        questions_count: count,
        max_possible: maxPossible,
        average: Math.round(average * 100) / 100,
        percentage: Math.round(percentage * 10) / 10,
        level: levelObj.level,
        level_label: levelObj.label,
        description: levelObj.description,
        primary_quadrants,
      };
    }

    if (questionsAnswered !== totalQuestions) {
      return {
        scale: 'SPAT',
        score: null,
        maxScore: 4,
        interpretation: null,
        severity: null,
        complete: false,
        message: `Incomplete assessment (${questionsAnswered}/${totalQuestions} statements answered)`,
        quadrant_scores: null,
        system_scores,
      };
    }

    const quadrant_scores = {};
    for (const quadrant of Object.keys(quadrantAcc)) {
      const { raw, count } = quadrantAcc[quadrant];
      const maxPossible = count * 5;
      const average = count > 0 ? raw / count : 0;
      const percentage = maxPossible > 0 ? (raw / maxPossible) * 100 : 0;
      const levelObj = determineLevelFromAverage(average);

      quadrant_scores[quadrant] = {
        raw_score: raw,
        questions_count: count,
        max_possible: maxPossible,
        average: Math.round(average * 100) / 100,
        percentage: Math.round(percentage * 10) / 10,
        level: levelObj.level,
        level_label: levelObj.label,
        description: levelObj.description,
      };
    }

    const highQuadrants = Object.keys(quadrant_scores).filter(q => quadrant_scores[q].average >= 3.5);
    const moderateQuadrants = Object.keys(quadrant_scores).filter(q => quadrant_scores[q].average >= 2.5 && quadrant_scores[q].average < 3.5);
    const clinicalSignificance = highQuadrants.length > 0 || (highQuadrants.length + moderateQuadrants.length) >= 2;

    // Elevated quadrants: all quadrants with average >= 2.5 (both high and moderate)
    // Per requirements doc Section 11.2: "Mixed pattern" when 2+ quadrants are elevated
    const elevated_quadrants = Object.keys(quadrant_scores)
      .filter(q => quadrant_scores[q].average >= 2.5)
      .sort((a, b) => quadrant_scores[b].average - quadrant_scores[a].average); // Sort by score descending
    
    // Mixed pattern: true when 2 or more quadrants are elevated (>= 2.5 average)
    const is_mixed_pattern = elevated_quadrants.length >= 2;

    // Dominant quadrant: highest scoring quadrant (kept for backward compatibility)
    // Note: When is_mixed_pattern is true, the report should show all elevated quadrants
    const dominant_quadrant = Object.keys(quadrant_scores)
      .sort((a, b) => quadrant_scores[b].average - quadrant_scores[a].average)[0];

    const elevated_systems = Object.keys(system_scores).filter(k => system_scores[k].average >= 3.5);

    // Compact numeric "score" for the provider progress bar:
    // count of quadrants that are at least moderate (>=2.5), range 0..4.
    const elevatedQuadrantCount = elevated_quadrants.length;

    // Build interpretation string based on pattern type
    const interpretationText = is_mixed_pattern
      ? `Mixed pattern: ${elevated_quadrants.map(q => q.charAt(0).toUpperCase() + q.slice(1)).join(', ')}.`
      : `Dominant quadrant: ${dominant_quadrant}.`;

    return {
      scale: 'SPAT',
      score: elevatedQuadrantCount,
      maxScore: 4,
      complete: true,
      severity: clinicalSignificance ? 'Clinically significant' : 'Not clinically significant',
      interpretation: interpretationText,
      quadrant_scores,
      system_scores,
      summary: {
        total_questions: totalQuestions,
        questions_answered: questionsAnswered,
        completion_percentage: 100,
        dominant_quadrant,
        elevated_quadrants,
        is_mixed_pattern,
        elevated_systems,
        clinical_significance: clinicalSignificance,
      },
    };
  } catch (error) {
    console.error('Error scoring SPAT:', error);
    return {
      scale: 'SPAT',
      score: null,
      maxScore: 4,
      interpretation: null,
      severity: null,
      complete: false,
      error: error.message,
    };
  }
};

/**
 * Scores the BAARS-IV Childhood assessment scale
 * 
 * BAARS-IV Childhood has 18 questions scored on a 4-point Likert scale:
 * Never or Rarely = 1, Sometimes = 2, Often = 3, Very Often = 4
 * 
 * The questions alternate between inattention (A1x) and hyperactivity-impulsivity (A2x):
 * - Inattention (A1a-A1i): Odd questions 1, 3, 5, 7, 9, 11, 13, 15, 17 (9 questions, max score 36)
 * - Hyperactivity-Impulsivity (A2a-A2i): Even questions 2, 4, 6, 8, 10, 12, 14, 16, 18 (9 questions, max score 36)
 * 
 * Total max score is 72 (36 + 36)
 * Clinical significance threshold is ≥ 48
 * 
 * @param {Object} formData - The formData object containing BAARS-IV Childhood responses
 * @returns {Object} The scoring results including total score, subscale scores, and interpretation
 */
export const scoreBaarsIVChildhood = (formData) => {
  if (!formData || !formData.formSubsections) return null;
  
  try {
    console.log(`[ScoreBaarsIVChildhood] Starting to score BAARS-IV Childhood`);
    
    // Get all answers from the form
    const answers = [];
    const subsection = formData.formSubsections['1'];
    
    // BAARS-IV Childhood has 18 questions
    for (let i = 1; i <= 18; i++) {
      const question = subsection.questions[i.toString()];
      if (question && question.subquestions && question.subquestions['1']) {
        const answerObj = question.subquestions['1'].answer;
        
        // Extract the value property from the answer object
        const answerValue = answerObj && typeof answerObj === 'object' ? answerObj.value : answerObj;
        answers.push(answerValue);
      }
    }
    
    // Calculate score: Never or Rarely = 1, Sometimes = 2, Often = 3, Very Often = 4
    const scoreMap = {
      "Never or Rarely": 1,
      "Sometimes": 2,
      "Often": 3,
      "Very Often": 4
    };
    
    // Define which questions belong to each subscale (using 1-based question numbers)
    // The form alternates: Q1=A1a (inattention), Q2=A2a (hyperactivity-impulsivity), etc.
    // Inattention (A1a-A1i): Odd questions 1, 3, 5, 7, 9, 11, 13, 15, 17
    const inattentionQuestions = [1, 3, 5, 7, 9, 11, 13, 15, 17];
    // Hyperactivity-Impulsivity (A2a-A2i): Even questions 2, 4, 6, 8, 10, 12, 14, 16, 18
    const hyperactivityImpulsivityQuestions = [2, 4, 6, 8, 10, 12, 14, 16, 18];
    
    let totalScore = 0;
    let validAnswers = 0;
    
    // Subscale scores
    let inattentionScore = 0;
    let hyperactivityImpulsivityScore = 0;
    
    // Count symptoms (any answer of "Often" or "Very Often") per subscale
    let totalSymptoms = 0;
    let inattentionSymptoms = 0;
    let hyperactivityImpulsivitySymptoms = 0;
    
    answers.forEach((answer, index) => {
      if (answer && scoreMap[answer] !== undefined) {
        const questionNumber = index + 1; // Convert 0-based index to 1-based question number
        const answerScore = scoreMap[answer];
        
        totalScore += answerScore;
        validAnswers++;
        
        // Determine which subscale this question belongs to and add to subscale score
        // Odd questions = Inattention, Even questions = Hyperactivity-Impulsivity
        if (inattentionQuestions.includes(questionNumber)) {
          inattentionScore += answerScore;
          if (answer === "Often" || answer === "Very Often") {
            inattentionSymptoms++;
            totalSymptoms++;
          }
        } else if (hyperactivityImpulsivityQuestions.includes(questionNumber)) {
          hyperactivityImpulsivityScore += answerScore;
          if (answer === "Often" || answer === "Very Often") {
            hyperactivityImpulsivitySymptoms++;
            totalSymptoms++;
          }
        }
      }
    });
    
    // Only calculate if we have all 18 answers
    if (validAnswers !== 18) {
      return {
        scale: 'BAARS-IV Childhood',
        score: null,
        interpretation: null,
        severity: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/18 questions answered)`
      };
    }
    
    // Determine clinical significance (≥ 48 suggests clinically significant ADHD symptoms)
    const isSignificant = totalScore >= 48;
    const interpretation = isSignificant 
      ? "Clinically significant childhood ADHD symptoms" 
      : "Not clinically significant childhood ADHD symptoms";
    const severity = isSignificant ? "Clinically Significant" : "Not Clinically Significant";

    return {
      scale: 'BAARS-IV Childhood',
      score: totalScore,
      maxScore: 72,      
      isSignificant,
      interpretation,
      severity,
      // Subscale scores for detailed analysis
      // Based on DSM-5 criteria: A1 = Inattention, A2 = Hyperactivity-Impulsivity
      subscales: {
        inattention: {
          score: inattentionScore,
          maxScore: 36, // 9 questions × 4 max points
          symptoms: inattentionSymptoms,
          maxSymptoms: 9
        },
        hyperactivityImpulsivity: {
          score: hyperactivityImpulsivityScore,
          maxScore: 36, // 9 questions × 4 max points
          symptoms: hyperactivityImpulsivitySymptoms,
          maxSymptoms: 9
        }
      },
      // Properties used in report generation (document_functions.py)
      totalSymptoms,
      inattentionSymptoms,
      hyperactivityImpulsivitySymptoms,
      complete: true
    };
  } catch (error) {
    console.error("Error scoring BAARS-IV Childhood:", error);
    return {
      scale: 'BAARS-IV Childhood',
      score: null,
      interpretation: null,
      severity: null,
      complete: false,
      error: error.message
    };
  }
};

// SNAP-IV Childhood (Informant) scoring
export const scoreSnapIVChildhood = (formData) => {
  if (!formData || !formData.formSubsections) return null;
  try {
    console.log(`[ScoreSnapIVChildhood] Starting to score SNAP-IV Childhood`);

    const answers = [];
    const subsection = formData.formSubsections['1'];

    // SNAP-IV has 18 questions (1-9 inattention, 10-18 hyperactivity/impulsivity)
    for (let i = 1; i <= 18; i++) {
      const question = subsection?.questions?.[i.toString()];
      if (question && question.subquestions && question.subquestions['1']) {
        const answerObj = question.subquestions['1'].answer;
        const answerValue = answerObj && typeof answerObj === 'object' ? answerObj.value : answerObj;
        answers.push(answerValue);
      } else {
        answers.push(undefined);
      }
    }

    // Likert mapping: Not at all = 0, Just a little = 1, Quite a bit = 2, Very much = 3
    const scoreMap = {
      'Not at all': 0,
      'Just a little': 1,
      'Quite a bit': 2,
      'Very much': 3,
    };

    let validAnswers = 0;
    let inattentionScore = 0; // Q1-9 (max 27)
    let hyperImpulsivityScore = 0; // Q10-18 (max 27)

    answers.forEach((answer, index) => {
      if (answer !== undefined && scoreMap[answer] !== undefined) {
        const score = scoreMap[answer];
        validAnswers++;
        if (index < 9) {
          inattentionScore += score; // 0-based index -> Q1-9
        } else {
          hyperImpulsivityScore += score; // Q10-18
        }
      }
    });

    if (validAnswers !== 18) {
      return {
        scale: 'SNAP-IV Childhood - Informant',
        totalScore: null,
        interpretation: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/18 questions answered)`,
      };
    }

    const classify = (subsetScore) => {
      if (subsetScore < 13) return 'Not Clinically Significant';
      if (subsetScore <= 17) return 'Mild';
      if (subsetScore <= 22) return 'Moderate';
      return 'Severe'; // >= 23 (covers 23-27)
    };

    const inattentionSeverity = classify(inattentionScore);
    const hyperImpulsivitySeverity = classify(hyperImpulsivityScore);
    const totalScore = inattentionScore + hyperImpulsivityScore; // max 54

    return {
      scale: 'SNAP-IV Childhood - Informant',
      totalScore,
      maxScore: 54,
      inattentionScore,
      hyperactivityImpulsivityScore: hyperImpulsivityScore,
      inattentionSeverity,
      hyperactivityImpulsivitySeverity: hyperImpulsivitySeverity,
      complete: true,
      subscales: {
        inattention: { score: inattentionScore, maxScore: 27, severity: inattentionSeverity },
        hyperactivityImpulsivity: { score: hyperImpulsivityScore, maxScore: 27, severity: hyperImpulsivitySeverity },
      },
    };
  } catch (error) {
    console.error('Error scoring SNAP-IV Childhood:', error);
    return {
      scale: 'SNAP-IV Childhood - Informant',
      totalScore: null,
      interpretation: null,
      complete: false,
      error: error.message,
    };
  }
};

/**
 * Scores the SNAP-IV 26 (CYP ADHD Informant) assessment scale.
 *
 * The CYP ADHD pathway uses the 26-item SNAP-IV rating CURRENT behaviour over
 * the past 6 months (unlike snapIVChildhoodInformant, the adult platform's
 * retrospective childhood scale). Items are laid out across three
 * subsections in the form definition (python-functions/constants/cyp_adhd/
 * scales/snap_iv_26.py):
 * - Subsection 1: Q1-9 inattention (max 27)
 * - Subsection 2: Q10-18 hyperactivity/impulsivity (max 27)
 * - Subsection 3: Q19-26 opposition/defiance (max 24)
 *
 * Likert mapping and severity thresholds are carried from the published RMS
 * snapIV26 schema (and rms_scoring.py): inattention and hyperactivity bands
 * at 13/18/23, opposition bands at 8/14/19. Total max score is 78.
 *
 * @param {Object} formData - The formData object containing SNAP-IV 26 responses
 * @returns {Object} The scoring results including total, subscale scores and severities
 */
export const scoreSnapIV26 = (formData) => {
  if (!formData || !formData.formSubsections) return null;
  try {
    // Collect answers keyed by item number, resolved from the serviceUserVarName
    // (snapIV26_Informant_Q{n}) so the scorer is robust to subsection layout.
    const answersByItem = {};
    Object.values(formData.formSubsections).forEach((subsection) => {
      Object.values(subsection?.questions || {}).forEach((question) => {
        Object.values(question?.subquestions || {}).forEach((subquestion) => {
          const match = /^snapIV26_Informant_Q(\d+)$/.exec(subquestion?.serviceUserVarName || '');
          if (!match) return;
          const answerObj = subquestion.answer;
          // Items 1-18 offer an optional example box behind the two highest
          // ratings, so the live runner stores those answers as
          // { selected, otherText } rather than a plain label. The score is
          // ONLY the rating: the text must never change a number, and an
          // object answer must not be mistaken for "unanswered" (which would
          // leave the whole scale unscored). `value` is the older ADHD /
          // demo shape, kept for the same reason as in the other scorers.
          answersByItem[Number(match[1])] =
            answerObj && typeof answerObj === 'object'
              ? (answerObj.selected ?? answerObj.value)
              : answerObj;
        });
      });
    });

    // Validated Likert mapping (RMS snapIV26 option scores).
    const scoreMap = {
      'Not at all': 0,
      'Just a little': 1,
      'Quite a bit': 2,
      'Very much': 3,
    };

    let validAnswers = 0;
    let inattentionScore = 0;        // Q1-9 (max 27)
    let hyperImpulsivityScore = 0;   // Q10-18 (max 27)
    let oppositionScore = 0;         // Q19-26 (max 24)

    for (let item = 1; item <= 26; item++) {
      const answer = answersByItem[item];
      if (answer === undefined || scoreMap[answer] === undefined) continue;
      validAnswers++;
      const score = scoreMap[answer];
      if (item <= 9) inattentionScore += score;
      else if (item <= 18) hyperImpulsivityScore += score;
      else oppositionScore += score;
    }

    if (validAnswers !== 26) {
      return {
        scale: 'SNAP-IV 26 - Informant',
        totalScore: null,
        interpretation: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/26 questions answered)`,
      };
    }

    // Severity bands from the published RMS snapIV26 scoring meta.
    const classifyAdhdSubscale = (score) => {
      if (score < 13) return 'Not Clinically Significant';
      if (score <= 17) return 'Mild';
      if (score <= 22) return 'Moderate';
      return 'Severe'; // 23-27
    };
    const classifyOpposition = (score) => {
      if (score < 8) return 'Not Clinically Significant';
      if (score <= 13) return 'Mild';
      if (score <= 18) return 'Moderate';
      return 'Severe'; // 19-24
    };

    const inattentionSeverity = classifyAdhdSubscale(inattentionScore);
    const hyperImpulsivitySeverity = classifyAdhdSubscale(hyperImpulsivityScore);
    const oppositionSeverity = classifyOpposition(oppositionScore);
    const totalScore = inattentionScore + hyperImpulsivityScore + oppositionScore; // max 78

    return {
      scale: 'SNAP-IV 26 - Informant',
      totalScore,
      maxScore: 78,
      inattentionScore,
      hyperactivityImpulsivityScore: hyperImpulsivityScore,
      oppositionDefianceScore: oppositionScore,
      inattentionSeverity,
      hyperactivityImpulsivitySeverity: hyperImpulsivitySeverity,
      oppositionDefianceSeverity: oppositionSeverity,
      complete: true,
      subscales: {
        inattention: { score: inattentionScore, maxScore: 27, severity: inattentionSeverity },
        hyperactivityImpulsivity: { score: hyperImpulsivityScore, maxScore: 27, severity: hyperImpulsivitySeverity },
        oppositionDefiance: { score: oppositionScore, maxScore: 24, severity: oppositionSeverity },
      },
    };
  } catch (error) {
    console.error('Error scoring SNAP-IV 26:', error);
    return {
      scale: 'SNAP-IV 26 - Informant',
      totalScore: null,
      interpretation: null,
      complete: false,
      error: error.message,
    };
  }
};

/**
 * Scores the BAARS-IV Current (Patient Self-Report) assessment scale
 * 
 * BAARS-IV Current has 18 questions scored on a 4-point Likert scale:
 * Never or Rarely = 1, Sometimes = 2, Often = 3, Very Often = 4
 * 
 * The questions alternate between inattention (A1x) and hyperactivity-impulsivity (A2x):
 * - Inattention (A1a-A1i): Odd questions 1, 3, 5, 7, 9, 11, 13, 15, 17 (9 questions, max score 36)
 * - Hyperactivity-Impulsivity (A2a-A2i): Even questions 2, 4, 6, 8, 10, 12, 14, 16, 18 (9 questions, max score 36)
 * 
 * Total max score is 72 (36 + 36)
 * Clinical significance threshold is ≥ 48
 * 
 * @param {Object} formData - The formData object containing BAARS-IV Current responses
 * @returns {Object} The scoring results including total score, subscale scores, and interpretation
 */
export const scoreBaarsIVCurrent = (formData) => {
  if (!formData || !formData.formSubsections) return null;
  
  try {
    console.log(`[ScoreBaarsIVCurrent] Starting to score BAARS-IV Current`);
    
    // Get all answers from the form
    const answers = [];
    const subsection = formData.formSubsections['1'];
    
    // BAARS-IV Current has 18 questions
    for (let i = 1; i <= 18; i++) {
      const question = subsection.questions[i.toString()];
      if (question && question.subquestions && question.subquestions['1']) {
        const answerObj = question.subquestions['1'].answer;
        
        // Extract the value property from the answer object
        const answerValue = answerObj && typeof answerObj === 'object' ? answerObj.value : answerObj;
        answers.push(answerValue);
      }
    }
    
    // Calculate score: Never or Rarely = 1, Sometimes = 2, Often = 3, Very Often = 4
    const scoreMap = {
      "Never or Rarely": 1,
      "Sometimes": 2,
      "Often": 3,
      "Very Often": 4
    };
    
    // Define which questions belong to each subscale (using 1-based question numbers)
    // The form alternates: Q1=A1a (inattention), Q2=A2a (hyperactivity-impulsivity), etc.
    // Inattention (A1a-A1i): Odd questions 1, 3, 5, 7, 9, 11, 13, 15, 17
    const inattentionQuestions = [1, 3, 5, 7, 9, 11, 13, 15, 17];
    // Hyperactivity-Impulsivity (A2a-A2i): Even questions 2, 4, 6, 8, 10, 12, 14, 16, 18
    const hyperactivityImpulsivityQuestions = [2, 4, 6, 8, 10, 12, 14, 16, 18];
    
    let totalScore = 0;
    let validAnswers = 0;
    
    // Subscale scores
    let inattentionScore = 0;
    let hyperactivityImpulsivityScore = 0;
    
    // Count symptoms (any answer of "Often" or "Very Often") per subscale
    let totalSymptoms = 0;
    let inattentionSymptoms = 0;
    let hyperactivityImpulsivitySymptoms = 0;
    
    answers.forEach((answer, index) => {
      if (answer && scoreMap[answer] !== undefined) {
        const questionNumber = index + 1; // Convert 0-based index to 1-based question number
        const answerScore = scoreMap[answer];
        
        totalScore += answerScore;
        validAnswers++;
        
        // Determine which subscale this question belongs to and add to subscale score
        // Odd questions = Inattention, Even questions = Hyperactivity-Impulsivity
        if (inattentionQuestions.includes(questionNumber)) {
          inattentionScore += answerScore;
          if (answer === "Often" || answer === "Very Often") {
            inattentionSymptoms++;
            totalSymptoms++;
          }
        } else if (hyperactivityImpulsivityQuestions.includes(questionNumber)) {
          hyperactivityImpulsivityScore += answerScore;
          if (answer === "Often" || answer === "Very Often") {
            hyperactivityImpulsivitySymptoms++;
            totalSymptoms++;
          }
        }
      }
    });
    
    // Only calculate if we have all 18 answers
    if (validAnswers !== 18) {
      return {
        scale: 'BAARS-IV Current',
        score: null,
        interpretation: null,
        severity: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/18 questions answered)`
      };
    }
    
    // Determine clinical significance (≥ 48 suggests clinically significant ADHD symptoms)
    const isSignificant = totalScore >= 48;
    const interpretation = isSignificant 
      ? "Clinically significant adulthood ADHD symptoms" 
      : "Not clinically significant adulthood ADHD symptoms";
    const severity = isSignificant ? "Clinically Significant" : "Not Clinically Significant";

    return {
      scale: 'BAARS-IV Current',
      score: totalScore,
      maxScore: 72,      
      isSignificant,
      interpretation,
      severity,
      // Subscale scores for detailed analysis
      // Based on DSM-5 criteria: A1 = Inattention, A2 = Hyperactivity-Impulsivity
      subscales: {
        inattention: {
          score: inattentionScore,
          maxScore: 36, // 9 questions × 4 max points
          symptoms: inattentionSymptoms,
          maxSymptoms: 9
        },
        hyperactivityImpulsivity: {
          score: hyperactivityImpulsivityScore,
          maxScore: 36, // 9 questions × 4 max points
          symptoms: hyperactivityImpulsivitySymptoms,
          maxSymptoms: 9
        }
      },
      // Properties used in report generation (document_functions.py)
      totalSymptoms,
      inattentionSymptoms,
      hyperactivityImpulsivitySymptoms,
      complete: true
    };
  } catch (error) {
    console.error("Error scoring BAARS-IV Current:", error);
    return {
      scale: 'BAARS-IV Current',
      score: null,
      interpretation: null,
      severity: null,
      complete: false,
      error: error.message
    };
  }
};


/**
 * Scores the PHQ-9 depression screening questionnaire
 * @param {Object} formData - The formData object containing PHQ-9 responses
 * @returns {Object} The scoring results including score, severity, and interpretation
 */
export const scorePHQ9 = (formData) => {
  if (!formData || !formData.formSubsections) return null;
  
  try {    
    // Get answers for questions 1-9
    const answers = [];
    const subsection = formData.formSubsections['1'];
    
    for (let i = 1; i <= 9; i++) {
      const question = subsection.questions[i.toString()];
      if (question && question.subquestions && question.subquestions['1']) {
        const answerObj = question.subquestions['1'].answer;
        console.log(`[ScoresPHQ9] Question ${i} answer:`, answerObj);
        
        // Extract the value property from the answer object
        const answerValue = answerObj && typeof answerObj === 'object' ? answerObj.value : answerObj;
        answers.push(answerValue);
      }
    }
    
    // Get difficulty question (question 10)
    const difficultyQuestion = subsection.questions['10'];
    const difficultyAnswerObj = difficultyQuestion?.subquestions?.['1']?.answer || null;
    const difficultyAnswer = difficultyAnswerObj && typeof difficultyAnswerObj === 'object' ? 
      difficultyAnswerObj.value : difficultyAnswerObj;
    
    console.log(`[ScoresPHQ9] Extracted answers:`, answers);
    
    // Calculate score (0-27)
    const scoreMap = {
      "Not at all": 0,
      "Several days": 1,
      "More than half the days": 2,
      "Nearly every day": 3
    };
    
    let totalScore = 0;
    let validAnswers = 0;
    
    answers.forEach((answer, index) => {
      console.log(`[ScoresPHQ9] Processing answer ${index + 1}:`, answer);
      if (answer && scoreMap[answer] !== undefined) {
        totalScore += scoreMap[answer];
        validAnswers++;
      }
    });
    
    console.log(`[ScoresPHQ9] Total valid answers: ${validAnswers}, Total score: ${totalScore}`);
    
    // Only calculate if we have all 9 answers
    if (validAnswers !== 9) {
      return {
        scale: 'PHQ-9',
        score: null,
        severity: null,
        interpretation: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/9 questions answered)`
      };
    }
    
    // Determine severity
    let severity, interpretation;
    if (totalScore >= 0 && totalScore <= 4) {
      severity = "Minimal";
      interpretation = "Minimal or no depression";
    } else if (totalScore >= 5 && totalScore <= 9) {
      severity = "Mild";
      interpretation = "Mild depression";
    } else if (totalScore >= 10 && totalScore <= 14) {
      severity = "Moderate";
      interpretation = "Moderate depression";
    } else if (totalScore >= 15 && totalScore <= 19) {
      severity = "Moderately Severe";
      interpretation = "Moderately severe depression";
    } else if (totalScore >= 20) {
      severity = "Severe";
      interpretation = "Severe depression";
    }
    
    // Add functional impairment info if available
    let impairment = null;
    if (difficultyAnswer) {
      impairment = difficultyAnswer;
    }
    
    return {
      scale: 'PHQ-9',
      score: totalScore,
      severity,
      interpretation,
      impairment,
      complete: true
    };
  } catch (error) {
    console.error("Error scoring PHQ-9:", error);
    return {
      scale: 'PHQ-9',
      score: null,
      severity: null, 
      interpretation: null,
      complete: false,
      error: error.message
    };
  }
};

export const scoreGAD7 = (formData) => {
  if (!formData || !formData.formSubsections) return null;
  
  try {
    console.log(`[ScoresGAD7] Starting to score GAD-7`);
    
    // Get answers for questions 1-7
    const answers = [];
    const subsection = formData.formSubsections['1'];
    
    for (let i = 1; i <= 7; i++) {
      const question = subsection.questions[i.toString()];
      if (question && question.subquestions && question.subquestions['1']) {
        const answerObj = question.subquestions['1'].answer;
        
        // Extract the value property from the answer object
        const answerValue = answerObj && typeof answerObj === 'object' ? answerObj.value : answerObj;
        answers.push(answerValue);
      }
    }
    
    // Get difficulty question (question 8)
    const difficultyQuestion = subsection.questions['8'];
    const difficultyAnswerObj = difficultyQuestion?.subquestions?.['1']?.answer || null;
    const difficultyAnswer = difficultyAnswerObj && typeof difficultyAnswerObj === 'object' ? 
      difficultyAnswerObj.value : difficultyAnswerObj;
    
    // Calculate score (0-21)
    const scoreMap = {
      "Not at all": 0,
      "Several days": 1,
      "More than half the days": 2,
      "Nearly every day": 3
    };
    
    let totalScore = 0;
    let validAnswers = 0;
    
    answers.forEach(answer => {
      if (answer && scoreMap[answer] !== undefined) {
        totalScore += scoreMap[answer];
        validAnswers++;
      }
    });
    
    // Only calculate if we have all 7 answers
    if (validAnswers !== 7) {
      return {
        scale: 'GAD-7',
        score: null,
        severity: null,
        interpretation: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/7 questions answered)`
      };
    }
    
    // Determine severity
    let severity, interpretation;
    if (totalScore >= 0 && totalScore <= 4) {
      severity = "Minimal";
      interpretation = "Minimal anxiety";
    } else if (totalScore >= 5 && totalScore <= 9) {
      severity = "Mild";
      interpretation = "Mild anxiety";
    } else if (totalScore >= 10 && totalScore <= 14) {
      severity = "Moderate";
      interpretation = "Moderate anxiety";
    } else if (totalScore >= 15) {
      severity = "Severe";
      interpretation = "Severe anxiety";
    }
    
    // Add functional impairment info if available
    let impairment = null;
    if (difficultyAnswer) {
      impairment = difficultyAnswer;
    }
    
    return {
      scale: 'GAD-7',
      score: totalScore,
      severity,
      interpretation,
      impairment,
      complete: true
    };
  } catch (error) {
    console.error("Error scoring GAD-7:", error);
    return {
      scale: 'GAD-7',
      score: null,
      severity: null, 
      interpretation: null,
      complete: false,
      error: error.message
    };
  }
};

export const scoreESS = (formData) => {
  if (!formData || !formData.formSubsections) return null;
  
  try {
    console.log(`[ScoresESS] Starting to score ESS`);
    
    // Get answers for all 8 questions
    const answers = [];
    const subsection = formData.formSubsections['1'];
    
    for (let i = 1; i <= 8; i++) {
      const question = subsection.questions[i.toString()];
      if (question && question.subquestions && question.subquestions['1']) {
        const answerObj = question.subquestions['1'].answer;
        
        // Extract the value property from the answer object
        const answerValue = answerObj && typeof answerObj === 'object' ? answerObj.value : answerObj;
        answers.push(answerValue);
      }
    }
    
    // Calculate score (0-24)
    const scoreMap = {
      "Would never doze": 0,
      "Slight chance of dozing": 1,
      "Moderate chance of dozing": 2,
      "High chance of dozing": 3
    };
    
    let totalScore = 0;
    let validAnswers = 0;
    
    answers.forEach(answer => {
      if (answer && scoreMap[answer] !== undefined) {
        totalScore += scoreMap[answer];
        validAnswers++;
      }
    });
    
    // Only calculate if we have all 8 answers
    if (validAnswers !== 8) {
      return {
        scale: 'ESS',
        score: null,
        interpretation: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/8 questions answered)`
      };
    }
    
    // Determine interpretation
    let interpretation;
    if (totalScore >= 0 && totalScore <= 5) {
      interpretation = "Lower Normal Daytime Sleepiness";
    } else if (totalScore >= 6 && totalScore <= 10) {
      interpretation = "Higher Normal Daytime Sleepiness";
    } else if (totalScore >= 11 && totalScore <= 12) {
      interpretation = "Mild Excessive Daytime Sleepiness";
    } else if (totalScore >= 13 && totalScore <= 15) {
      interpretation = "Moderate Excessive Daytime Sleepiness";
    } else if (totalScore >= 16) {
      interpretation = "Severe Excessive Daytime Sleepiness";
    }
    
    return {
      scale: 'ESS',
      score: totalScore,
      interpretation,
      complete: true
    };
  } catch (error) {
    console.error("Error scoring ESS:", error);
    return {
      scale: 'ESS',
      score: null,
      interpretation: null,
      complete: false,
      error: error.message
    };
  }
};

export const scoreMDQ = (formData) => {
  if (!formData || !formData.formSubsections) return null;
  
  try {
    console.log(`[ScoresMDQ] Starting to score MDQ`);
    
    // Get answers for questions 1.1 through 1.13 (section 1, first 13 subquestions)
    const subsection = formData.formSubsections['1'];
    const question = subsection.questions['1'];
    
    if (!question || !question.subquestions) {
      return {
        scale: 'MDQ',
        score: null,
        isPositive: null,
        interpretation: null,
        complete: false,
        message: "Questions not found"
      };
    }
    
    // Count "Yes" answers in part 1 (first 13 subquestions)
    let yesCount = 0;
    let validAnswers = 0;
    
    for (let i = 1; i <= 13; i++) {
      if (question.subquestions[i.toString()]) {
        const answerObj = question.subquestions[i.toString()].answer;
        
        // Extract the value property from the answer object
        const answerValue = answerObj && typeof answerObj === 'object' ? answerObj.value : answerObj;
        
        if (answerValue === "Yes") {
          yesCount++;
        }
        if (answerValue === "Yes" || answerValue === "No") {
          validAnswers++;
        }
      }
    }
    
    // Get co-occurrence answer from question 2
    const coOccurrenceQuestion = subsection.questions['2'];
    const coOccurrenceAnswerObj = coOccurrenceQuestion?.subquestions?.['1']?.answer || null;
    const coOccurrenceAnswer = coOccurrenceAnswerObj && typeof coOccurrenceAnswerObj === 'object' ? 
      coOccurrenceAnswerObj.value : coOccurrenceAnswerObj;
    
    // Get problem severity answer from question 3
    const problemSeverityQuestion = subsection.questions['3'];
    const problemSeverityAnswerObj = problemSeverityQuestion?.subquestions?.['1']?.answer || null;
    const problemSeverityAnswer = problemSeverityAnswerObj && typeof problemSeverityAnswerObj === 'object' ? 
      problemSeverityAnswerObj.value : problemSeverityAnswerObj;
    
    // Check if assessment is complete
    if (validAnswers !== 13 || !coOccurrenceAnswer || !problemSeverityAnswer) {
      return {
        scale: 'MDQ',
        score: null,
        isPositive: null,
        impairment: null,
        interpretation: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/13 symptom questions answered)`
      };
    }
    
    // MDQ is positive if:
    // 1. 7 or more "Yes" responses to questions 1-13
    // 2. "Yes" to "symptoms happened during the same period of time"
    // 3. "Moderate" or "Serious" problem severity
    
    const criteriaOne = yesCount >= 7;
    const criteriaTwo = coOccurrenceAnswer === "Yes";
    const criteriaThree = problemSeverityAnswer === "Moderate problem" || problemSeverityAnswer === "Serious problem";
    
    const isPositive = criteriaOne && criteriaTwo && criteriaThree;
    
    let interpretation;
    if (isPositive) {
      interpretation = "Positive screen for bipolar spectrum disorder. Clinical follow-up is indicated.";
    } else {
      interpretation = "Negative screen for bipolar spectrum disorder.";
    }

    // Determine severity based on positive/negative screen
    const severity = isPositive ? "Clinically Significant" : "Not Clinically Significant";
    
    return {
      scale: 'MDQ',
      score: yesCount, // Add score property for UI compatibility
      maxScore: 13,
      isPositive,
      yesCount,
      coOccurrence: criteriaTwo,
      problemSeverity: problemSeverityAnswer,
      impairment: problemSeverityAnswer, // Add impairment property for UI compatibility
      severity, // Add severity property for UI compatibility
      interpretation,
      complete: true,
      criteria: {
        symptomCount: criteriaOne,
        coOccurrence: criteriaTwo,
        problemSeverity: criteriaThree
      }
    };
  } catch (error) {
    console.error("Error scoring MDQ:", error);
    return {
      scale: 'MDQ',
      score: null,
      isPositive: null,
      interpretation: null,
      impairment: null,
      complete: false,
      error: error.message
    };
  }
};

/**
 * Scores the Y-BOCS (Yale-Brown Obsessive Compulsive Scale)
 * @param {Object} formData - The formData object containing Y-BOCS responses
 * @returns {Object} The scoring results including score, severity, and interpretation
 */
export const scoreYBOCS = (formData) => {
  if (!formData || !formData.formSubsections) return null;
  
  try {
    console.log(`[ScoresYBOCS] Starting to score Y-BOCS`);
    
    // Get all answers from the form
    const answers = [];
    const subsection = formData.formSubsections['1'];
    
    // Y-BOCS has 10 questions
    for (let i = 1; i <= 10; i++) {
      const question = subsection.questions[i.toString()];
      if (question && question.subquestions && question.subquestions['1']) {
        const answerObj = question.subquestions['1'].answer;
        
        // Extract the value property from the answer object
        const answerValue = answerObj && typeof answerObj === 'object' ? answerObj.value : answerObj;
        answers.push(answerValue);
      }
    }
    
    // Calculate score (0-4 for each question)
    const scoreMap = {
      "0": 0,
      "1": 1,
      "2": 2,
      "3": 3,
      "4": 4,
      // Handle written answers
      "None": 0,
      "0-1 hrs/day": 1, "Mild": 1, "Little": 1, "Try much of the time": 1, "Much control": 1,
      "1-3 hrs/day": 2, "Definite but manageable": 2, "Moderate but manageable": 2, "Try some of the time": 2, "Some control": 2,
      "3-8 hrs/day": 3, "Substantial interference": 3, "Severe": 3, "Rarely try. Often yield": 3, "Little control": 3, 
      "More than 8 hrs/day": 4, "Severe interference": 4, "Nearly constant, Disabling": 4, "Never try. Completely yield": 4, "No control": 4,
      "Always try": 0, "Complete control": 0
    };
    
    let totalScore = 0;
    let validAnswers = 0;
    
    answers.forEach((answer, index) => {
      if (answer && scoreMap[answer] !== undefined) {
        totalScore += scoreMap[answer];
        validAnswers++;
      }
    });
    
    // Calculate obsession and compulsion subscales
    let obsessionScore = 0;
    let compulsionScore = 0;
    
    // Questions 1-5 are obsession questions
    for (let i = 0; i < 5; i++) {
      if (answers[i] && scoreMap[answers[i]] !== undefined) {
        obsessionScore += scoreMap[answers[i]];
      }
    }
    
    // Questions 6-10 are compulsion questions
    for (let i = 5; i < 10; i++) {
      if (answers[i] && scoreMap[answers[i]] !== undefined) {
        compulsionScore += scoreMap[answers[i]];
      }
    }
    
    // Only calculate if we have all 10 answers
    if (validAnswers !== 10) {
      return {
        scale: 'Y-BOCS',
        score: null,
        severity: null,
        interpretation: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/10 questions answered)`
      };
    }
    
    // Determine severity based on total score
    let severity, interpretation;
    if (totalScore < 8) {
      severity = "Subclinical";
      interpretation = "Symptoms are below the clinical threshold for OCD";
    } else if (totalScore >= 8 && totalScore <= 15) {
      severity = "Mild";
      interpretation = "Mild OCD symptoms";
    } else if (totalScore >= 16 && totalScore <= 23) {
      severity = "Moderate";
      interpretation = "Moderate OCD symptoms";
    } else if (totalScore >= 24 && totalScore <= 31) {
      severity = "Severe";
      interpretation = "Severe OCD symptoms";
    } else {
      severity = "Extreme";
      interpretation = "Extreme OCD symptoms";
    }
    
    return {
      scale: 'Y-BOCS',
      score: totalScore,
      maxScore: 40,
      obsessionScore,
      compulsionScore,
      severity,
      interpretation,
      complete: true,
      subscales: {
        obsessions: {
          score: obsessionScore,
          maxScore: 20
        },
        compulsions: {
          score: compulsionScore,
          maxScore: 20
        }
      }
    };
  } catch (error) {
    console.error("Error scoring Y-BOCS:", error);
    return {
      scale: 'Y-BOCS',
      score: null,
      severity: null,
      interpretation: null,
      complete: false,
      error: error.message
    };
  }
};

/**
 * Scores the AUDIT-C (Alcohol Use Disorders Identification Test - Consumption)
 * @param {Object} formData - The formData object containing AUDIT-C responses
 * @returns {Object} The scoring results including score, interpretation, and risk level
 */
export const scoreAUDITC = (formData) => {
  if (!formData || !formData.formSubsections) return null;
  
  try {
    console.log(`[ScoresAUDITC] Starting to score AUDIT-C`);
    
    // Get answers for questions 1-3
    const answers = [];
    const subsection = formData.formSubsections['1'];
    
    for (let i = 1; i <= 3; i++) {
      const question = subsection.questions[i.toString()];
      if (question && question.subquestions && question.subquestions['1']) {
        const answerObj = question.subquestions['1'].answer;
        
        // Extract the value property from the answer object
        const answerValue = answerObj && typeof answerObj === 'object' ? answerObj.value : answerObj;
        answers.push(answerValue);
      }
    }
    
    // Calculate score (0-12)
    const scoreMap = {
      // Question 1: How often did you have a drink containing alcohol in the past year?
      "Never": 0,
      "Monthly or less": 1,
      "Two to four times a month": 2,
      "Two to three times per week": 3,
      "Four or more times a week": 4,
      
      // Question 2: How many drinks containing alcohol did you have on a typical day when you were drinking in the past year?
      "1 or 2 drinks": 0,
      "3 or 4": 1,
      "5 or 6": 2,
      "7 to 9": 3,
      "10 or more": 4,
      
      // Question 3: How often did you have six or more drinks on one occasion in the past year?
      // "Never": 0, // Already defined above
      "Less than monthly": 1,
      "Monthly": 2,
      "Weekly": 3,
      "Daily or almost daily": 4
    };
    
    let totalScore = 0;
    let validAnswers = 0;
    
    answers.forEach(answer => {
      if (answer && scoreMap[answer] !== undefined) {
        totalScore += scoreMap[answer];
        validAnswers++;
      }
    });
    
    // Only calculate if we have all 3 answers
    if (validAnswers !== 3) {
      return {
        scale: 'AUDIT-C',
        score: null,
        interpretation: null,
        severity: null,
        complete: false,
        message: `Incomplete assessment (${validAnswers}/3 questions answered)`
      };
    }
    
    // Determine interpretation based on score
    let interpretation, severity;
    if (totalScore < 3) {
      interpretation = "Consistent with normal alcohol consumption";
      severity = "Normal";
    } else {
      // Note: The interpretation would ideally consider gender, but we'll use the general threshold
      // For females: ≥3 indicates alcohol misuse
      // For males: ≥4 indicates alcohol misuse
      interpretation = "Consistent with alcohol misuse (at-risk drinking)";
      severity = "At-Risk";
    }
    
    return {
      scale: 'AUDIT-C',
      score: totalScore,
      maxScore: 12,
      interpretation,
      severity,
      complete: true,
      riskLevel: totalScore >= 3 ? "At-Risk" : "Normal"
    };
  } catch (error) {
    console.error("Error scoring AUDIT-C:", error);
    return {
      scale: 'AUDIT-C',
      score: null,
      interpretation: null,
      severity: null,
      complete: false,
      error: error.message
    };
  }
};

/**
 * Scores the WEISS (Weiss Functional Impairment Rating Scale)
 * @param {Object} formData - The formData object containing WEISS responses
 * @returns {Object} The scoring results including section scores and overall impairment
 */
export const scoreWeiss = (formData) => {
  if (!formData || !formData.formSubsections) return null;
  
  try {
    console.log(`[ScoreWeiss] Starting to score WEISS`);
    
    const subsection = formData.formSubsections['1'];
    if (!subsection || !subsection.questions) {
      return {
        scale: 'WEISS',
        complete: false,
        message: "Questions not found"
      };
    }
    
    // Define the sections and their question counts
    const sections = {
      family: { questionKey: '1', questionCount: 8, name: 'Family' },
      work: { questionKey: '2', questionCount: 11, name: 'Work' },
      education: { questionKey: '3', questionCount: 10, name: 'Education' },
      lifeSkills: { questionKey: '4', questionCount: 12, name: 'Life Skills' },
      selfConcept: { questionKey: '5', questionCount: 5, name: 'Self-Concept' },
      social: { questionKey: '6', questionCount: 9, name: 'Social' },
      risk: { questionKey: '7', questionCount: 14, name: 'Risk' }
    };
    
    // Score mapping
    const scoreMap = {
      "Never": 0,
      "Sometimes": 1,
      "Often": 2,
      "Very Often": 3
      // "Not Applicable" is ignored (not included in calculation)
    };
    
    const sectionResults = {};
    let totalValidSections = 0;
    let totalImpairedSections = 0;
    
    // Process each section
    Object.keys(sections).forEach(sectionKey => {
      const section = sections[sectionKey];
      const question = subsection.questions[section.questionKey];
      
      if (!question || !question.subquestions) {
        sectionResults[sectionKey] = {
          name: section.name,
          score: null,
          averageScore: null,
          isImpaired: null,
          validAnswers: 0,
          totalQuestions: section.questionCount,
          complete: false
        };
        return;
      }
      
      let sectionTotal = 0;
      let validAnswers = 0;
      
      // Process each subquestion in the section
      for (let i = 1; i <= section.questionCount; i++) {
        const subquestion = question.subquestions[i.toString()];
        if (subquestion && subquestion.answer) {
          const answerObj = subquestion.answer;
          const answerValue = answerObj && typeof answerObj === 'object' ? answerObj.value : answerObj;
          
          // Only include answers that are not "Not Applicable"
          if (answerValue && answerValue !== "Not Applicable" && scoreMap[answerValue] !== undefined) {
            sectionTotal += scoreMap[answerValue];
            validAnswers++;
          }
        }
      }
      
      // Calculate average score for this section
      const averageScore = validAnswers > 0 ? sectionTotal / validAnswers : null;
      const isImpaired = averageScore !== null ? averageScore > 1.5 : null;
      const isComplete = validAnswers > 0; // At least one valid answer to consider section complete
      
      sectionResults[sectionKey] = {
        name: section.name,
        score: sectionTotal,
        averageScore: averageScore,
        isImpaired: isImpaired,
        validAnswers: validAnswers,
        totalQuestions: section.questionCount,
        complete: isComplete
      };
      
      if (isComplete) {
        totalValidSections++;
        if (isImpaired) {
          totalImpairedSections++;
        }
      }
      
      console.log(`[ScoreWeiss] ${section.name}: ${validAnswers}/${section.questionCount} valid answers, avg: ${averageScore?.toFixed(2)}, impaired: ${isImpaired}`);
    });
    
    // Determine overall completion
    const isComplete = totalValidSections > 0;
    
    return {
      scale: 'WEISS',
      sectionResults: sectionResults,
      totalValidSections: totalValidSections,
      totalImpairedSections: totalImpairedSections,
      complete: isComplete,
      summary: {
        family: sectionResults.family,
        work: sectionResults.work,
        education: sectionResults.education,
        lifeSkills: sectionResults.lifeSkills,
        selfConcept: sectionResults.selfConcept,
        social: sectionResults.social,
        risk: sectionResults.risk
      }
    };
  } catch (error) {
    console.error("Error scoring WEISS:", error);
    return {
      scale: 'WEISS',
      complete: false,
      error: error.message
    };
  }
};

/**
 * Determines age range from date of birth
 * @param {string} dateOfBirth - Date of birth in YYYY-MM-DD format
 * @returns {string} Age range (18_34, 35_49, 50_64, 65_81) or null if outside range
 */
const getAgeRange = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  const age = today.getFullYear() - birthDate.getFullYear() - 
    (today.getMonth() < birthDate.getMonth() || 
     (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate()) ? 1 : 0);
  
  if (age >= 18 && age <= 34) return '18_34';
  if (age >= 35 && age <= 49) return '35_49';
  if (age >= 50 && age <= 64) return '50_64';
  if (age >= 65 && age <= 81) return '65_81';
  
  return null; // Outside supported age ranges
};

/**
 * Loads and parses BDEFS lookup table CSV data
 * @param {string} ageRange - Age range (18_34, 35_49, 50_64, 65_81)
 * @param {string} sex - Sex assigned at birth (Male, Female)
 * @returns {Promise<Array>} Parsed lookup table data
 */
const loadBDEFSLookupTable = async (ageRange, sex) => {
  try {
    const fileName = `${ageRange}_${sex.toLowerCase()}.csv`;
    const response = await fetch(`/lookupTables/${fileName}`);
    
    if (!response.ok) {
      console.warn(`[BDEFS] Lookup table not found: ${fileName}`);
      return null;
    }
    
    const csvText = await response.text();
    console.log(`[BDEFS] CSV text length: ${csvText.length}, first 200 chars:`, csvText.substring(0, 200));
    
    const lines = csvText.trim().split('\n');
    console.log(`[BDEFS] Number of lines: ${lines.length}`);
    
    if (lines.length < 2) {
      console.error(`[BDEFS] CSV file ${fileName} has insufficient data: ${lines.length} lines`);
      return null;
    }
    
    const headers = lines[0].split(',');
    console.log(`[BDEFS] Headers:`, headers);
    
    const data = lines.slice(1).map((line, index) => {
      const values = line.split(',');
      const row = {};
      headers.forEach((header, headerIndex) => {
        row[header.trim()] = values[headerIndex]?.trim() || '';
      });
      if (index < 3) { // Log first few rows for debugging
        console.log(`[BDEFS] Row ${index + 1}:`, row);
      }
      return row;
    });
    
    console.log(`[BDEFS] Loaded lookup table: ${fileName} with ${data.length} rows`);
    return data;
  } catch (error) {
    console.error(`[BDEFS] Error loading lookup table:`, error);
    return null;
  }
};

/**
 * Parses score range (handles single numbers, ranges, and dashes)
 * @param {string} scoreStr - Score string from CSV (e.g., "60", "60-63", "-")
 * @returns {Object} {min, max, valid} where valid indicates if score is usable
 */
const parseScoreRange = (scoreStr) => {
  if (!scoreStr || scoreStr === '-' || scoreStr.trim() === '') {
    return { min: null, max: null, valid: false };
  }
  
  if (scoreStr.includes('-')) {
    const [min, max] = scoreStr.split('-').map(s => parseInt(s.trim()));
    return { min, max, valid: !isNaN(min) && !isNaN(max) };
  }
  
  // Handle special case like "99+"
  if (scoreStr.includes('+')) {
    const num = parseInt(scoreStr.replace('+', ''));
    return { min: num, max: Infinity, valid: !isNaN(num) };
  }
  
  const num = parseInt(scoreStr);
  return { min: num, max: num, valid: !isNaN(num) };
};

/**
 * Formats percentile for display (handles both single values and ranges)
 * @param {number|Array|null} percentile - Single number, [min, max] range, or null
 * @returns {string} Formatted percentile string
 */
const formatPercentileDisplay = (percentile) => {
  if (percentile === null || percentile === undefined) return 'No percentile found';
  if (Array.isArray(percentile)) return `${percentile[0]}-${percentile[1]}th percentile range`;
  return `${percentile}th percentile`;
};

/**
 * Finds percentile for a given score using lookup table
 * @param {number} score - The raw score to look up
 * @param {Array} lookupTable - The parsed lookup table data
 * @param {string} columnName - The column name to search in
 * @returns {number|Array|null} The percentile (single number, [min, max] range, or null if not found)
 */
const findPercentile = (score, lookupTable, columnName) => {
  if (!lookupTable || !score || !columnName) return null;
  
  for (const row of lookupTable) {
    const scoreRange = parseScoreRange(row[columnName]);
    if (!scoreRange.valid) continue;
    
    if (score >= scoreRange.min && score <= scoreRange.max) {
      const percentileStr = row['%'];
      
      // Handle percentile ranges like "51-75"
      if (percentileStr.includes('-')) {
        const [min, max] = percentileStr.split('-').map(s => parseInt(s.trim()));
        return [min, max]; // Return as tuple [lower, upper]
      }
      
      // Handle "99+"
      if (percentileStr.includes('+')) {
        return 99;
      }
      
      const percentile = parseInt(percentileStr);
      return isNaN(percentile) ? null : percentile;
    }
  }
  
  return null;
};

/**
 * Scores the BDEFS (Barkley Deficits in Executive Functioning Scale)
 * @param {Object} formData - The formData object containing BDEFS responses
 * @param {Object} patientData - Patient data including dateOfBirth and sexAssignedAtBirth
 * @returns {Object} The scoring results including section scores, ADHD likelihood, and percentiles
 */
export const scoreBDEFS = async (formData, patientData = null) => {
  if (!formData || !formData.formSubsections) return null;
  
  try {
    console.log(`[ScoreBDEFS] Starting to score BDEFS`);
    
    const subsection = formData.formSubsections['1'];
    if (!subsection || !subsection.questions) {
      return {
        scale: 'BDEFS',
        complete: false,
        message: "Questions not found"
      };
    }
    
    // Define the sections and their question ranges
    const sections = {
      selfManagementTime: { 
        questionKey: '1', 
        questionCount: 21, 
        name: 'Self-Management to Time',
        questionRange: [1, 21] 
      },
      selfOrganisation: { 
        questionKey: '2', 
        questionCount: 24, 
        name: 'Self-Organisation / Problem Solving',
        questionRange: [22, 45] 
      },
      selfRestraint: { 
        questionKey: '3', 
        questionCount: 19, 
        name: 'Self-Restraint',
        questionRange: [46, 64] 
      },
      selfMotivation: { 
        questionKey: '4', 
        questionCount: 12, 
        name: 'Self-Motivation',
        questionRange: [65, 76] 
      },
      selfRegulationEmotion: { 
        questionKey: '5', 
        questionCount: 13, 
        name: 'Self-Regulation of Emotion',
        questionRange: [77, 89] 
      }
    };
    
    // Score mapping
    const scoreMap = {
      "Never or rarely": 1,
      "Sometimes": 2,
      "Often": 3,
      "Very often": 4
    };
    
    // ADHD likelihood questions (by global question number)
    const adhdLikelihoodQuestions = [1, 6, 14, 16, 24, 49, 50, 55, 60, 65, 69];
    
    const sectionResults = {};
    let totalValidSections = 0;
    
    // Process each section
    Object.keys(sections).forEach(sectionKey => {
      const section = sections[sectionKey];
      const question = subsection.questions[section.questionKey];
      
      if (!question || !question.subquestions) {
        sectionResults[sectionKey] = {
          name: section.name,
          score: null,
          validAnswers: 0,
          totalQuestions: section.questionCount,
          complete: false
        };
        return;
      }
      
      let sectionTotal = 0;
      let validAnswers = 0;
      
      // Process each subquestion in the section
      for (let i = 1; i <= section.questionCount; i++) {
        const subquestion = question.subquestions[i.toString()];
        if (subquestion && subquestion.answer) {
          const answerObj = subquestion.answer;
          const answerValue = answerObj && typeof answerObj === 'object' ? answerObj.value : answerObj;
          
          if (answerValue && scoreMap[answerValue] !== undefined) {
            sectionTotal += scoreMap[answerValue];
            validAnswers++;
          }
        }
      }
      
      const isComplete = validAnswers > 0;
      
      sectionResults[sectionKey] = {
        name: section.name,
        score: sectionTotal,
        validAnswers: validAnswers,
        totalQuestions: section.questionCount,
        complete: isComplete
      };
      
      if (isComplete) {
        totalValidSections++;
      }
      
      console.log(`[ScoreBDEFS] ${section.name}: ${validAnswers}/${section.questionCount} valid answers, total score: ${sectionTotal}`);
    });
    
    // Calculate ADHD likelihood score
    let adhdLikelihoodScore = 0;
    let adhdValidAnswers = 0;
    
    // Helper function to get global question number from section and local question
    const getGlobalQuestionNumber = (sectionKey, localQuestionNumber) => {
      const section = sections[sectionKey];
      return section.questionRange[0] + localQuestionNumber - 1;
    };
    
    // Process ADHD likelihood questions
    Object.keys(sections).forEach(sectionKey => {
      const section = sections[sectionKey];
      const question = subsection.questions[section.questionKey];
      
      if (question && question.subquestions) {
        for (let i = 1; i <= section.questionCount; i++) {
          const globalQuestionNumber = getGlobalQuestionNumber(sectionKey, i);
          
          // Check if this is an ADHD likelihood question
          if (adhdLikelihoodQuestions.includes(globalQuestionNumber)) {
            const subquestion = question.subquestions[i.toString()];
            if (subquestion && subquestion.answer) {
              const answerObj = subquestion.answer;
              const answerValue = answerObj && typeof answerObj === 'object' ? answerObj.value : answerObj;
              
              if (answerValue && scoreMap[answerValue] !== undefined) {
                adhdLikelihoodScore += scoreMap[answerValue];
                adhdValidAnswers++;
                console.log(`[ScoreBDEFS] ADHD Q${globalQuestionNumber}: ${answerValue} = ${scoreMap[answerValue]}`);
              }
            }
          }
        }
      }
    });
    
    const isComplete = totalValidSections > 0;
    
    // Attempt percentile lookup if patient data is available
    let lookupTable = null;
    let percentiles = {};
    
    if (patientData && patientData.dateOfBirth && patientData.sexAssignedAtBirth) {
      const ageRange = getAgeRange(patientData.dateOfBirth);
      const sex = patientData.sexAssignedAtBirth;
      
      console.log(`[ScoreBDEFS] Patient age range: ${ageRange}, sex: ${sex}`);
      
      if (ageRange && (sex === 'Male' || sex === 'Female')) {
        try {
          lookupTable = await loadBDEFSLookupTable(ageRange, sex);
          
          if (lookupTable) {
            // Column mapping for lookup table
            const columnMapping = {
              selfManagementTime: 'Self-Management To Time',
              selfOrganisation: 'Self Organisation / Problem Solving',
              selfRestraint: 'Self-Restraint',
              selfMotivation: 'Self-Motivation',
              selfRegulationEmotion: 'Self-Regulation of Emotions'
            };
            
            // Calculate percentiles for each section
            Object.keys(sectionResults).forEach(sectionKey => {
              const sectionResult = sectionResults[sectionKey];
              const columnName = columnMapping[sectionKey];
              
              if (sectionResult.complete && columnName && sectionResult.score > 0) {
                const percentile = findPercentile(sectionResult.score, lookupTable, columnName);
                percentiles[sectionKey] = percentile;
                
                // Add percentile to section result
                sectionResults[sectionKey].percentile = percentile;
                
                console.log(`[ScoreBDEFS] ${sectionResult.name}: score ${sectionResult.score} = ${formatPercentileDisplay(percentile)}`);
              }
            });
            
            // Calculate ADHD likelihood percentile
            if (adhdLikelihoodScore > 0) {
              const adhdPercentile = findPercentile(adhdLikelihoodScore, lookupTable, 'ADHD-EF Index');
              percentiles.adhdLikelihood = adhdPercentile;
              console.log(`[ScoreBDEFS] ADHD Likelihood: score ${adhdLikelihoodScore} = ${formatPercentileDisplay(adhdPercentile)}`);
            }
          }
        } catch (error) {
          console.error(`[ScoreBDEFS] Error during percentile lookup:`, error);
        }
      } else {
        console.warn(`[ScoreBDEFS] Cannot perform percentile lookup - unsupported age range (${ageRange}) or sex (${sex})`);
      }
    } else {
      console.warn(`[ScoreBDEFS] Cannot perform percentile lookup - missing patient data (dateOfBirth: ${!!patientData?.dateOfBirth}, sexAssignedAtBirth: ${!!patientData?.sexAssignedAtBirth})`);
    }
    
    return {
      scale: 'BDEFS',
      sectionResults: sectionResults,
      adhdLikelihoodScore: adhdLikelihoodScore,
      adhdValidAnswers: adhdValidAnswers,
      adhdTotalQuestions: adhdLikelihoodQuestions.length,
      totalValidSections: totalValidSections,
      complete: isComplete,
      percentiles: percentiles,
      lookupTableUsed: lookupTable ? true : false,
      patientAge: patientData ? getAgeRange(patientData.dateOfBirth) : null,
      patientSex: patientData ? patientData.sexAssignedAtBirth : null,
      summary: {
        selfManagementTime: sectionResults.selfManagementTime,
        selfOrganisation: sectionResults.selfOrganisation,
        selfRestraint: sectionResults.selfRestraint,
        selfMotivation: sectionResults.selfMotivation,
        selfRegulationEmotion: sectionResults.selfRegulationEmotion,
        adhdLikelihood: {
          score: adhdLikelihoodScore,
          validAnswers: adhdValidAnswers,
          totalQuestions: adhdLikelihoodQuestions.length,
          complete: adhdValidAnswers > 0,
          percentile: percentiles.adhdLikelihood || null
        }
      }
    };
  } catch (error) {
    console.error("Error scoring BDEFS:", error);
    return {
      scale: 'BDEFS',
      complete: false,
      error: error.message
    };
  }
};

/**
 * Main function to score any scale based on the form key
 * @param {string} formKey - The key identifying which form/scale to score
 * @param {Object} formData - The complete form data containing responses
 * @param {Object} patientData - Optional patient data for enhanced scoring (includes dateOfBirth, sexAssignedAtBirth)
 * @returns {Promise<Object|null>} The scoring results for the specified scale
 */
export const scoreScale = async (formKey, formData, patientData = null) => {
  console.log(`[ScalesFunctions] Attempting to score scale: ${formKey}`);
  console.log(`[ScalesFunctions] Form data structure:`, JSON.stringify(formData, null, 2).substring(0, 500) + '...');
  
  let result = null;
  
  switch (formKey) {
    case 'phq9':
      console.log(`[ScalesFunctions] Scoring PHQ9`);
      result = scorePHQ9(formData);
      break;
    case 'gad7':
      console.log(`[ScalesFunctions] Scoring GAD7`);
      result = scoreGAD7(formData);
      break;
    case 'ess':
      console.log(`[ScalesFunctions] Scoring ESS`);
      result = scoreESS(formData);
      break;
    case 'mdq':
      console.log(`[ScalesFunctions] Scoring MDQ`);
      result = scoreMDQ(formData);
      break;
    case 'aq10':
      console.log(`[ScalesFunctions] Scoring AQ-10`);
      result = scoreAQ10(formData);
      break;
    case 'aq50':
      console.log(`[ScalesFunctions] Scoring AQ-50`);
      result = scoreAQ50(formData);
      break;
    case 'catq':
      console.log(`[ScalesFunctions] Scoring CAT-Q`);
      result = scoreCATQ(formData);
      break;
    case 'core10':
      console.log(`[ScalesFunctions] Scoring CORE-10`);
      result = scoreCORE10(formData);
      break;
    case 'asrs':
      console.log(`[ScalesFunctions] Scoring ASRS`);
      result = scoreASRS(formData);
      break;
    case 'cnst':
      console.log(`[ScalesFunctions] Scoring CNST`);
      result = scoreCNST(formData);
      break;
    case 'spat':
      console.log(`[ScalesFunctions] Scoring SPAT`);
      result = scoreSPAT(formData);
      break;
    case 'baarsIVChildhood':
      console.log(`[ScalesFunctions] Scoring BAARS-IV Childhood`);
      result = scoreBaarsIVChildhood(formData);
      break;
    case 'baarsIVCurrent':
      console.log(`[ScalesFunctions] Scoring BAARS-IV Current`);
      result = scoreBaarsIVCurrent(formData);
      break;
    case 'baarsIVChildhoodInformant':
      console.log(`[ScalesFunctions] Scoring BAARS-IV Childhood (Informant)`);
      result = scoreBaarsIVChildhood(formData);
      break;
    case 'snapIVChildhoodInformant':
      console.log(`[ScalesFunctions] Scoring SNAP-IV Childhood (Informant)`);
      result = scoreSnapIVChildhood(formData);
      break;
    case 'snapIV26Informant':
      console.log(`[ScalesFunctions] Scoring SNAP-IV 26 (Informant)`);
      result = scoreSnapIV26(formData);
      break;
    case 'baarsIVCurrentInformant':
      console.log(`[ScalesFunctions] Scoring BAARS-IV Current (Informant)`);
      result = scoreBaarsIVCurrent(formData);
      break;
    case 'asrsOAdulthoodInformant':
      console.log(`[ScalesFunctions] Scoring ASRS-O Informant`);
      result = scoreASRSOInformant(formData);
      break;
    case 'ybocs':
      console.log(`[ScalesFunctions] Scoring Y-BOCS`);
      result = scoreYBOCS(formData);
      break;
    case 'auditc':
      console.log(`[ScalesFunctions] Scoring AUDIT-C`);
      result = scoreAUDITC(formData);
      break;
    case 'weiss':
      console.log(`[ScalesFunctions] Scoring WEISS`);
      result = scoreWeiss(formData);
      break;
    case 'bdefs':
      console.log(`[ScalesFunctions] Scoring BDEFS`);
      result = await scoreBDEFS(formData, patientData);
      break;
    default:
      console.warn(`[ScalesFunctions] No scoring function available for scale: ${formKey}`);
      return null;
    }
    
    console.log(`[ScalesFunctions] Score result for ${formKey}:`, result);
    return result;
  };

/**
 * Get the display name for a scale based on its key
 * @param {string} scaleKey - The form key for the scale
 * @returns {string} The human-readable name of the scale
 */
export const getScaleDisplayName = (scaleKey) => {
  const scaleNames = {
    'phq9': 'Patient Health Questionnaire (PHQ-9)',
    'gad7': 'Generalized Anxiety Disorder Assessment (GAD-7)',
    'ess': 'Epworth Sleepiness Scale (ESS)',
    'mdq': 'Mood Disorder Questionnaire (MDQ)',
    'baarsIVChildhood': 'Your Symptoms in Childhood',
    'baarsIVCurrent': 'Your Symptoms in Adulthood',
    'baarsIVChildhoodInformant': 'Their Symptoms in Childhood',
    'snapIVChildhoodInformant': 'Their Symptoms in Childhood',
    'snapIV26Informant': 'ADHD Symptoms Scale (SNAP-IV 26)',
    'baarsIVCurrentInformant': 'Their Symptoms in Adulthood',
    'asrsOAdulthoodInformant': 'Their Symptoms in Adulthood',
    'ybocs': 'Yale-Brown Obsessive Compulsive Scale (Y-BOCS)',
    'aq10': 'Autism Spectrum Quotient (AQ-10)',
    'aq50': 'Autism Spectrum Quotient (AQ-50)',
    'catq': 'Camouflaging Autistic Traits Questionnaire (CAT-Q)',
    'core10': 'Clinical Outcomes in Routine Evaluation (CORE-10)',
    'asrs': 'Your Symptoms in Adulthood',
    'cnst': 'Combined Neurodevelopmental Screening Tool (CNST)',
    'spat': 'Sensory Profile Assessment Tool (SPAT)',
    'auditc': 'Alcohol Use Disorders Identification Test (AUDIT-C)',
    'weiss': 'Your Life Impact',
    'bdefs': 'Your Daily Life',
  };
  
  return scaleNames[scaleKey] || scaleKey;
};

/**
 * Determines if a form is a scorable clinical scale
 * @param {string} formKey - The key identifying the form
 * @returns {boolean} True if the form is a clinical scale with a scoring function
 */
export const isScorableScale = (formKey) => {
  const scorableScales = ['aq10','aq50','catq','core10','asrs','cnst','spat','phq9', 'gad7', 'ess', 'mdq', 'ybocs', 'baarsIVChildhood', 'baarsIVCurrent', 'baarsIVChildhoodInformant', 'baarsIVCurrentInformant', 'snapIVChildhoodInformant', 'snapIV26Informant', 'asrsOAdulthoodInformant', 'auditc', 'weiss', 'bdefs'];  
  return scorableScales.includes(formKey);
};